<template>
    <div
        :style="cssVars"
        :class="{
            'validation-wrapper': Boolean(hasErrors || applyErrorBorder),
            'mb-5': ! noMargin
        }"
        :id="id"
    >
        <label
            v-if="$slots.default"
            @click="$refs.input.focus()"
            class="flex font-semibold flex-row items-center space-x-2 mb-1 ml-2 text-sm text-gray-700 mt-5 tracking-wide"
            :class="labelClasses"
        >
            <slot/>
            <span v-if="required" class="text-red-600">*</span>
        </label>
        <div class="flex flex-col justify-center items-center">
            <input
                :type="type"
                :name="props.name"
                ref="input"
                class="validated relative"
                @input="$emit('update:modelValue', $event.target.value)"
                @change="$emit('update:modelValue', $event.target.value)"
                @focus="$emit('focus')"
                v-model="value"
                @blur="$emit('blur')"
                @click="$emit('click')"
                :class="inputClasses"
                :disabled="disabled"
                :readonly="readonly"
                :placeholder="placeholder"
                :inputmode="inputmode"
                :maxlength="maxLength"
                :autocomplete="autocomplete"
            />
            <div
                class="relative -mt-8 cursor-pointer text-gray-400 self-end mr-3"
                v-if="type === 'search'"
                @click="$emit('input', null)"
            >
                <fa-icon
                    icon="fas fa-times"
                />
            </div>
            <small class="text-gray-400 self-start" v-if="$slots['small-message']">
                <slot name="small-message"/>
            </small>
        </div>

        <div class="mb-5" v-if="errorMessage && ! props.hideErrors">
            <small class="text-red-500 mt-1">
                {{ errorMessage }}
            </small>
        </div>

        <div
            class="mb-5"
            v-if="hasErrors"
        >
            <small
                class="text-red-500 mt-1"
                v-for="error in errors"
            >
                {{ error }}
            </small>
        </div>
    </div>
</template>

<script lang="ts" setup>

import {
    useTemplateRef,
    reactive,
    onMounted,
    computed,
    watch
} from 'vue'

import { useField } from 'vee-validate'

const emit = defineEmits([
     'update:modelValue',
    'click',
    'focus',
    'blur'
])

const props = defineProps({
    modelValue: String,
    id: {
        required: false,
        type: String,
    },
    name: {
        required: false,
        type: String,
        default: undefined
    },
    rules: {
        required: false,
        default: undefined
    },
    placeholder: {
        required: false,
        type: String,
    },
    labelClasses: {
        required: false,
        type: String
    },
    inputClasses: {
        required: false,
        type: String
    },
    errorKey: {
        required: false,
        type: String
    },
    errorBag: {
        required: false,
        type: Object,
        default: () => ({})
    },
    readonly: {
        required: false,
        type: Boolean,
        default: false,
    },
    disabled: {
        required: false,
        type: Boolean,
        default: false,
    },
    autoFocus: {
        required: false,
        type: Boolean,
        default: false,
    },
    type: {
        required: false,
        default: 'text'
    },
    maxLength: {
        requried: false,
        default: 524288, // Default for input field
        type: Number
    },
    noMargin: {
        required: false,
        type: Boolean,
        default: false
    },
    applyErrorBorder: {
        required: false,
        type: Boolean,
        default: false
    },
    required: {
        type: Boolean,
        default: false
    },
    cssValues: {
        required: false,
        type: Object,
        /*
        /
        / To add a new CSS Variable, add an identifier here, and then add the variable name with it's value
        / to the computed property 'cssVars'
        / It can then be used in the style block
        /
        */
        default: () => ({
            'border-width': '2px',
            'border-top': '2px',
            'border-right': '2px',
            'border-bottom': '2px',
            'border-left': '2px',
        })
    },
    hideErrors: {
        required: false,
        type: Boolean,
        default: false,
    },
    inputmode: {
        required: false,
        type: String,
        default: undefined,
    },
    autocomplete: {
        required: false,
        type: String,
        default: undefined,
    },
})

const input = useTemplateRef('input')
onMounted(() => {
    if (props.autoFocus) input
        .value
        .focus()
})

const cssVars = computed(() => ({
    '--border-top': props.cssValues['border-top'] ?? props.cssValues['border-width'],
    '--border-right': props.cssValues['border-right'] ?? props.cssValues['border-width'],
    '--border-bottom': props.cssValues['border-bottom'] ?? props.cssValues['border-width'],
    '--border-left': props.cssValues['border-left'] ?? props.cssValues['border-width'],
}))

const { value, errorMessage, setErrors } = useField(
    () => props.name,
    props.rules
        ? reactive(props.rules)
        : undefined,
    { syncVModel: true }
)

const hasErrors = computed(() => {
    if (errorMessage.value) {
        return true
    }

    return (props.errorKey !== false)
         ? props.errorBag.hasOwnProperty(props.errorKey)
         : false
})

watch(() => props.errorBag, (errors) =>
{
    if (!Object.keys(errors).length) {
        return
    }

    setErrors(Object.entries(errors)[0][1])
});

const errors = computed(() => {
    return Array.isArray(props.errorBag[props.errorKey])
        ? props.errorBag[props.errorKey]
        : [props.errorBag[props.errorKey]]
})

</script>

<style lang="scss" scoped>

input {
    @apply border rounded-md border-gray-300 text-gray-700 placeholder:text-gray-400 text-sm;

    height: auto;
    width: 100%;
    appearance: none;
    border-radius: 4px;
    appearance: none;
    border-radius: 4px;

    border-width:
        var(--border-top)
        var(--border-right)
        var(--border-bottom)
        var(--border-left);

    --tw-border-opacity: 1;
    border-color: rgb(209 213 219 / var(--tw-border-opacity));
    padding-top: 8px;
    padding-bottom: 8px;
    padding-left: 16px;
    padding-right: 16px;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.25;

    transition-property: color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter, -webkit-backdrop-filter;
    transition-duration: 300ms;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);

    &:focus {
        --tw-border-opacity: 1;
        border-color: rgb(107 114 128 / var(--tw-border-opacity));
        --tw-bg-opacity: 1;
        background-color: rgb(255 255 255 / var(--tw-bg-opacity));
        outline: 2px solid transparent;
        outline-offset: 2px;
        --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
        --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(0px + var(--tw-ring-offset-width)) var(--tw-ring-color);
        box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000);
    }

    &:disabled {
        @apply bg-gray-100;
    }
}

.validation-wrapper {
    .validated:not(:disabled) {
        @apply border border-red-500;
    }
}

input[type="search"]::-webkit-search-decoration,
input[type="search"]::-webkit-search-cancel-button,
input[type="search"]::-webkit-search-results-button,
input[type="search"]::-webkit-search-results-decoration {
  -webkit-appearance:none;
}

</style>
